import React, { useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { AppContext, SttDialog, SttForm } from '../../../sporttia/all';
import { usePrevAndUpdatedStates } from '../../../lib/utils';
import { usePermission } from '../../../lib/usePermission';
import { PERMISSIONS } from '../../../lib/permissions';

type Props = {
    product: any;
    autoClose?: boolean;
    onClose: () => void;
    onUpdateProduct?: (product: any) => void;
    onDeleteProduct?: (product: any) => void;
};

export default function ProductDialog({
    product,
    autoClose = true,
    onClose,
    onUpdateProduct,
    onDeleteProduct,
}: Props): React.ReactElement {
    const cxt = useContext(AppContext)!;
    const { t } = useTranslation();
    const { can } = usePermission();
    const canManageProducts = can(PERMISSIONS.SALE_POINT.MODIFY);
    const [internalProduct, setInternalProduct] = useState<any>(product);
    const { setPrevState, setUpdatedState, updatedState } =
        usePrevAndUpdatedStates(product);

    /**
     * Set product
     */
    useEffect(() => {
        setInternalProduct(product);
        setPrevState(product);
    }, [product, setPrevState]);

    /**
     * Save product
     */
    const saveProduct = () => {
        const params: Record<string, unknown> = {
            name: internalProduct.name,
            description: internalProduct.description,
            category: internalProduct.category,
            finalPrice: internalProduct.finalPrice,
            stockageActive: internalProduct.stockageActive,
            vat: internalProduct.vat,
        };

        // Only include stock if it has a valid value
        if (
            internalProduct.stock !== undefined &&
            internalProduct.stock !== null &&
            internalProduct.stock !== ''
        ) {
            params.stock = internalProduct.stock;
        }
        // Updating
        if (internalProduct.id) {
            cxt?.api('PUT', `/products/${internalProduct.id}`, {
                params,
                success: (r: any) => {
                    cxt?.showMessage('S', t('Saved'));
                    setPrevState(updatedState);
                    if (onUpdateProduct) onUpdateProduct(r.product);
                    if (autoClose) onClose();
                },
            });

            // Creating
        } else {
            cxt?.api('POST', `/scs/${cxt?.sc?.id}/products`, {
                params,
                success: (r: any) => {
                    cxt?.showMessage('S', t('Created'));
                    if (onUpdateProduct) onUpdateProduct(r.product);
                    if (autoClose) onClose();
                },
            });
        }
    };

    /**
     * Delete product
     */
    const deleteProduct = () => {
        cxt?.api('DELETE', `/products/${internalProduct.id}`, {
            confirmation: true,
            success: (r: any) => {
                cxt?.showMessage('S', t('Deleted'));
                if (onDeleteProduct) onDeleteProduct(r.product);
                if (autoClose) onClose();
            },
        });
    };

    /**
     * Handle form
     */
    const handleForm = ({
        name,
        value,
    }: {
        name: string | undefined;
        value: unknown;
    }) => {
        if (!name) return;
        setInternalProduct({ ...internalProduct, [name]: value });
        setUpdatedState({ ...updatedState, [name]: value });
    };

    // -----| Render |-----
    return (
        <SttDialog
            title={t('Product')}
            open={product !== undefined && product !== null}
            onClose={onClose}
            content={
                internalProduct && (
                    <SttForm
                        onChangeForm={handleForm}
                        elements={[
                            {
                                md: 12,
                                sm: 12,
                                xs: 12,
                                caption: t('Name'),
                                name: 'name',
                                value: internalProduct.name,
                            },
                            {
                                md: 12,
                                sm: 12,
                                xs: 12,
                                caption: t('Description'),
                                type: 'textarea',
                                name: 'description',
                                value: internalProduct.description,
                            },
                            {
                                md: 9,
                                sm: 6,
                                xs: 12,
                                caption: t('Category'),
                                name: 'category',
                                value: internalProduct.category,
                            },
                            {
                                md: 3,
                                sm: 6,
                                xs: 12,
                                caption: t('Stock'),
                                name: 'stock',
                                value: internalProduct.stock,
                            },
                            {
                                disabled: true,
                                md: 4,
                                sm: 4,
                                xs: 12,
                                caption: t('NetPrice'),
                                name: 'netPrice',
                                value: internalProduct.netPrice,
                            },
                            {
                                md: 4,
                                sm: 4,
                                xs: 12,
                                caption: t('VAT'),
                                name: 'vat',
                                value: internalProduct.vat,
                            },
                            {
                                md: 4,
                                sm: 4,
                                xs: 12,
                                caption: t('Price'),
                                name: 'finalPrice',
                                value: internalProduct.finalPrice,
                            },
                            {
                                md: 6,
                                sm: 6,
                                xs: 12,
                                type: 'check',
                                caption: t('Stockage'),
                                name: 'stockageActive',
                                value: internalProduct.stockageActive,
                            },
                        ]}
                    />
                )
            }
            buttons={[
                {
                    type: 'delete',
                    show:
                        canManageProducts &&
                        internalProduct &&
                        internalProduct.id > 0,
                    onClick: () => deleteProduct(),
                },
                {
                    type: 'save',
                    show: canManageProducts,
                    onClick: () => saveProduct(),
                },
                {
                    type: 'close',
                    onClick: onClose,
                },
            ]}
        />
    );
}
